import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { RitualIcon } from '@/components/ui/RitualIcon';
import { cn } from '@/lib/utils';

const agentMessages = [
  "3 overdue tasks. Want me to reassign?",
  "I noticed you skip vendor approvals on Mondays. Creating a pattern?",
  "Daily checklist completed 2 hours early. Nice work!",
  "New SOP suggestion based on your recent tasks.",
];

export function AgentIntro() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [currentMessage, setCurrentMessage] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (!isInView) return;

    const message = agentMessages[currentMessage];
    let charIndex = 0;
    setIsTyping(true);
    setDisplayText('');

    const typeInterval = setInterval(() => {
      if (charIndex <= message.length) {
        setDisplayText(message.slice(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
        
        // Move to next message after delay
        setTimeout(() => {
          setCurrentMessage((prev) => (prev + 1) % agentMessages.length);
        }, 3000);
      }
    }, 40);

    return () => clearInterval(typeInterval);
  }, [currentMessage, isInView]);

  return (
    <div ref={ref} className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
      {/* Text content */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.5 }}
        className="order-2 lg:order-1"
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-6">
          Meet your co-pilot.
        </h2>
        
        <div className="space-y-4 text-lg text-muted-foreground">
          <p>
            Ritual includes an AI agent that learns your patterns.
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="text-primary font-medium">→</span>
              It schedules runs.
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-medium">→</span>
              It nudges blockers.
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-medium">→</span>
              It writes new SOPs from your voice.
            </li>
          </ul>
          <p className="font-medium text-foreground pt-4">
            You don't manage operations.<br />
            You answer questions.
          </p>
        </div>
      </motion.div>

      {/* Agent visualization */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="order-1 lg:order-2 flex flex-col items-center"
      >
        {/* Agent avatar */}
        <motion.div
          className={cn(
            "w-20 h-20 rounded-full",
            "bg-primary",
            "flex items-center justify-center",
            "shadow-lg",
            "relative z-10"
          )}
          animate={{ scale: isTyping ? [1, 1.05, 1] : 1 }}
          transition={{ duration: 0.3, repeat: isTyping ? Infinity : 0, repeatDelay: 0.2 }}
        >
          <RitualIcon size="xl" />
          
          {/* Status indicator */}
          <div className={cn(
            "absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-background",
            isTyping ? "bg-accent animate-pulse" : "bg-success"
          )} />
        </motion.div>

        {/* Chat bubble - positioned in flow, not absolute */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: -10 }}
          animate={isInView ? { opacity: 1, scale: 1, y: 0 } : {}}
          transition={{ delay: 0.5, duration: 0.3 }}
          className="mt-4 w-full max-w-[280px] sm:max-w-xs"
        >
          <div className="bg-card border-2 border-border rounded-2xl rounded-tl-sm p-4 shadow-lg relative">
            {/* Bubble pointer */}
            <div className="absolute -top-2 left-6 w-4 h-4 bg-card border-l-2 border-t-2 border-border rotate-45" />
            
            <p className="text-sm text-foreground min-h-[2.5rem] relative z-10 break-words">
              {displayText}
              {isTyping && (
                <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse" />
              )}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
